import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Badge, type BadgeTone } from '@artosera/ui';
import { ETATS_ABONNEMENT, PALIERS } from '@artosera/shared';
import { listerGaleries, listerGroupes } from '../../lib/api.js';
import { montantEnSaisie, pourcentageEnSaisie } from '../../lib/montant.js';
import type { AbonnementSaisie, EtatAbonnement } from '../../types.js';
import { CelluleMontant, EtatOnglet, formaterJour, useLangue, useMessageErreur } from './commun.js';
import tableStyles from '../../styles/table.module.css';
import { TableauSaisie, type Brouillon, type ChampSaisie } from './TableauSaisie.js';
import { useSaisies } from './useSaisies.js';

const NATURES = ['galerie', 'forfait_exploitant'] as const;

const TON_ETAT: Record<EtatAbonnement, BadgeTone> = {
  essai: 'accent',
  actif: 'available',
  suspendu: 'reserved',
  resilie: 'withdrawn',
};

/**
 * Les abonnements (exploitation, direction) : celui d'UNE galerie à un palier,
 * ou le forfait d'un exploitant. À la création, si la grille a un tarif actif
 * pour le palier choisi, son montant est PROPOSÉ dans le champ — modifiable,
 * effaçable ; sans tarif saisi, le champ reste vide. Un abonnement ne se
 * supprime pas (l'API n'a pas de `DELETE`) : on le passe à « Résilié ».
 *
 * Un forfait d'exploitant se crée en choisissant l'exploitant (`GET /groupes`),
 * puis ses galeries (cochées d'office, aucune de plus) : `groupeId` et
 * `galeries` partent, pas de palier. Le montant proposé vient de la tranche de
 * la grille qui contient le nombre de galeries cochées ; une saisie à la main
 * n'est jamais écrasée.
 */
export function AbonnementsTab({ enEcriture }: { enEcriture: boolean }): JSX.Element {
  const { t } = useTranslation('console');
  const langue = useLangue();
  const message = useMessageErreur('abonnements');
  const abonnements = useSaisies('abonnements');
  const tarifs = useSaisies('tarifs');
  const galeries = useQuery({ queryKey: ['console', 'galeries'], queryFn: listerGaleries });
  // Les exploitants ne bloquent pas l'onglet : sans eux, seul le forfait manque.
  const groupes = useQuery({ queryKey: ['console', 'groupes'], queryFn: listerGroupes });
  const exploitants = groupes.data ?? [];

  const nomsGaleries = new Map((galeries.data ?? []).map((galerie) => [galerie.id, galerie.nom]));
  const nomDe = (id: string | undefined): string =>
    (id !== undefined ? nomsGaleries.get(id) : undefined) ??
    t('finances.abonnements.galerieInconnue');

  /** Le tarif actif du palier, s'il est saisi : le plus en tête de la grille. */
  const proposition = (palier: string): string => {
    const tarif = tarifs.lignes
      .filter(
        (candidat) =>
          candidat.type === 'palier' &&
          candidat.palier === palier &&
          candidat.actif &&
          candidat.montantMensuelCentimes !== null,
      )
      .sort((a, b) => a.ordre - b.ordre)[0];
    return tarif ? montantEnSaisie(tarif.montantMensuelCentimes) : '';
  };

  /** Le tarif actif de la tranche de forfait qui contient `nombre` galeries. */
  const propositionTranche = (nombre: number): string => {
    const tarif = tarifs.lignes
      .filter(
        (candidat) =>
          candidat.type === 'forfait_exploitant' &&
          candidat.actif &&
          candidat.montantMensuelCentimes !== null &&
          candidat.galeriesMin !== null &&
          candidat.galeriesMin <= nombre &&
          (candidat.galeriesMax === null || nombre <= candidat.galeriesMax),
      )
      .sort((a, b) => a.ordre - b.ordre)[0];
    return tarif ? montantEnSaisie(tarif.montantMensuelCentimes) : '';
  };

  const champs = (ligne: AbonnementSaisie | null): ChampSaisie[] => {
    const deGalerie = ligne === null || ligne.nature === 'galerie';
    const communs: ChampSaisie[] = [
      {
        cle: 'montantMensuelCentimes',
        titre: t('finances.abonnements.champs.montant'),
        type: 'montant',
      },
      {
        cle: 'remisePointsDeBase',
        titre: t('finances.abonnements.champs.remise'),
        type: 'pourcentage',
        videVaut: 0,
      },
      // Le montant est un HT : la TVA s'en déduit. 20 % au départ, 0 % permis.
      {
        cle: 'tauxTvaPointsDeBase',
        titre: t('finances.abonnements.champs.tva'),
        type: 'pourcentage',
        requis: true,
        initiale: '20',
        aide: t('finances.abonnements.tvaAide'),
      },
      {
        cle: 'engagementMois',
        titre: t('finances.abonnements.champs.engagement'),
        type: 'entier',
        requis: true,
      },
      {
        cle: 'debutLe',
        titre: t('finances.abonnements.champs.debut'),
        type: 'date',
        requis: true,
      },
      { cle: 'finLe', titre: t('finances.abonnements.champs.fin'), type: 'date' },
      {
        cle: 'etat',
        titre: t('finances.abonnements.champs.etat'),
        type: 'choix',
        requis: true,
        initiale: 'actif',
        options: ETATS_ABONNEMENT.map((etat) => ({
          value: etat,
          label: t(`finances.abonnements.etat.${etat}`),
        })),
      },
    ];
    if (ligne !== null && !deGalerie) return communs;
    const choixGalerie: ChampSaisie = {
      cle: 'galerie',
      cleCorps: 'galeries',
      titre: t('finances.abonnements.champs.galerie'),
      type: 'choix',
      requis: true,
      creationSeule: true,
      // Un espace de test ne se facture pas (lot B) : l'API le refuserait.
      options: (galeries.data ?? [])
        .filter((galerie) => !galerie.estTest)
        .map((galerie) => ({ value: galerie.id, label: galerie.nom })),
      enCorps: (valeur) => [valeur],
      visible: (brouillon) => ligne !== null || brouillon['nature'] !== 'forfait_exploitant',
    };
    const choixPalier: ChampSaisie = {
      cle: 'palier',
      titre: t('finances.abonnements.champs.palier'),
      type: 'choix',
      requis: true,
      aide: t('finances.abonnements.palierAide'),
      options: PALIERS.map((palier) => ({ value: palier, label: t(`galeries.palier.${palier}`) })),
      visible: (brouillon) => ligne !== null || brouillon['nature'] !== 'forfait_exploitant',
    };
    if (ligne !== null) return [choixGalerie, choixPalier, ...communs];
    const sousForfait = (brouillon: Brouillon): boolean =>
      brouillon['nature'] === 'forfait_exploitant';
    return [
      {
        cle: 'nature',
        titre: t('finances.abonnements.champs.nature'),
        type: 'choix',
        requis: true,
        creationSeule: true,
        initiale: 'galerie',
        options: NATURES.map((nature) => ({
          value: nature,
          label: t(`finances.abonnements.nature.${nature}`),
        })),
      },
      choixGalerie,
      choixPalier,
      {
        cle: 'groupeId',
        titre: t('finances.abonnements.champs.groupe'),
        type: 'choix',
        requis: true,
        creationSeule: true,
        options: exploitants.map((groupe) => ({ value: groupe.id, label: groupe.nom })),
        visible: sousForfait,
      },
      {
        cle: 'galeriesForfait',
        cleCorps: 'galeries',
        titre: t('finances.abonnements.champs.galeriesForfait'),
        type: 'cases',
        requis: true,
        creationSeule: true,
        aide: t('finances.abonnements.forfaitPropose'),
        optionsDe: (brouillon) =>
          (exploitants.find((groupe) => groupe.id === brouillon['groupeId'])?.galeries ?? []).map(
            (galerie) => ({ value: galerie.id, label: galerie.nom }),
          ),
        messageSansOption: t('finances.abonnements.forfaitSansGalerie'),
        enCorps: (valeur) => String(valeur).split(','),
        visible: (brouillon) => sousForfait(brouillon) && brouillon['groupeId'] !== '',
      },
      ...communs,
    ];
  };

  const galeriesCochees = (brouillon: Brouillon): number => {
    const texte = brouillon['galeriesForfait'];
    return typeof texte === 'string' && texte !== '' ? texte.split(',').length : 0;
  };

  /** Ce qui dépend d'un autre champ s'impose : changer d'exploitant recoche ses galeries. */
  const surChangement = (cle: string, valeur: string | boolean): Brouillon => {
    if (cle === 'nature') return { galerie: '', palier: '', groupeId: '', galeriesForfait: '' };
    if (cle === 'groupeId') {
      const groupe = exploitants.find((candidat) => candidat.id === valeur);
      return { galeriesForfait: (groupe?.galeries ?? []).map((galerie) => galerie.id).join(',') };
    }
    return {};
  };

  /**
   * À la création : le tarif du palier choisi, ou de la tranche du forfait, est
   * PROPOSÉ (jamais imposé : un champ touché n'est plus écrasé) ; sans tarif
   * saisi, le champ reste vide.
   */
  const proposer = (cle: string, valeur: string | boolean, brouillon: Brouillon): Brouillon => {
    if (cle === 'nature') return { montantMensuelCentimes: '' };
    if (cle === 'palier' && typeof valeur === 'string') {
      return { montantMensuelCentimes: proposition(valeur) };
    }
    if (cle === 'groupeId' || cle === 'galeriesForfait') {
      return { montantMensuelCentimes: propositionTranche(galeriesCochees(brouillon)) };
    }
    return {};
  };

  const chargement = abonnements.chargement || tarifs.chargement;
  const enErreur = abonnements.enErreur || tarifs.enErreur;
  if (chargement || enErreur) return <EtatOnglet chargement={chargement} enErreur={enErreur} />;

  return (
    <TableauSaisie<AbonnementSaisie>
      legende={t('finances.onglets.abonnements')}
      introduction={t('finances.abonnements.texte')}
      lignes={abonnements.lignes}
      colonnes={[
        {
          cle: 'galerie',
          titre: t('finances.abonnements.colonnes.galerie'),
          rendu: (ligne) =>
            ligne.nature === 'galerie' ? (
              nomDe(ligne.galeries[0])
            ) : (
              <>
                {t('finances.abonnements.forfait')}
                <span className={tableStyles.celluleMuette}>
                  {' · '}
                  {exploitants.find((groupe) => groupe.id === ligne.groupeId)?.nom}
                  {exploitants.some((groupe) => groupe.id === ligne.groupeId) ? ' · ' : null}
                  {t('finances.abonnements.forfaitGaleries', { count: ligne.galeries.length })}
                </span>
              </>
            ),
        },
        {
          cle: 'palier',
          titre: t('finances.abonnements.colonnes.palier'),
          rendu: (ligne) => (ligne.palier ? t(`galeries.palier.${ligne.palier}`) : '—'),
        },
        {
          cle: 'montant',
          titre: t('finances.abonnements.colonnes.montant'),
          numerique: true,
          rendu: (ligne) => <CelluleMontant centimes={ligne.montantMensuelCentimes} />,
        },
        {
          cle: 'remise',
          titre: t('finances.abonnements.colonnes.remise'),
          numerique: true,
          rendu: (ligne) =>
            ligne.remisePointsDeBase > 0
              ? `${pourcentageEnSaisie(ligne.remisePointsDeBase)} %`
              : '—',
        },
        {
          cle: 'tva',
          titre: t('finances.abonnements.colonnes.tva'),
          numerique: true,
          rendu: (ligne) => `${pourcentageEnSaisie(ligne.tauxTvaPointsDeBase)} %`,
        },
        {
          cle: 'engagement',
          titre: t('finances.abonnements.colonnes.engagement'),
          rendu: (ligne) => t('finances.abonnements.mois', { count: ligne.engagementMois }),
        },
        {
          cle: 'debut',
          titre: t('finances.abonnements.colonnes.debut'),
          rendu: (ligne) => formaterJour(ligne.debutLe, langue),
        },
        {
          cle: 'etat',
          titre: t('finances.abonnements.colonnes.etat'),
          rendu: (ligne) => (
            <Badge tone={TON_ETAT[ligne.etat]}>
              {t(`finances.abonnements.etat.${ligne.etat}`)}
            </Badge>
          ),
        },
      ]}
      champs={champs}
      valeursDe={(ligne) => ({
        galerie: ligne.galeries[0] ?? null,
        palier: ligne.palier,
        montantMensuelCentimes: ligne.montantMensuelCentimes,
        remisePointsDeBase: ligne.remisePointsDeBase,
        tauxTvaPointsDeBase: ligne.tauxTvaPointsDeBase,
        engagementMois: ligne.engagementMois,
        debutLe: ligne.debutLe,
        finLe: ligne.finLe,
        etat: ligne.etat,
      })}
      libelleLigne={(ligne) =>
        ligne.nature === 'galerie' ? nomDe(ligne.galeries[0]) : t('finances.abonnements.forfait')
      }
      enEcriture={enEcriture}
      libelleAjouter={t('finances.abonnements.ajouter')}
      messageVide={t('finances.abonnements.vide')}
      sansMontant={
        abonnements.lignes.filter((ligne) => ligne.montantMensuelCentimes === null).length
      }
      proposer={proposer}
      surChangement={surChangement}
      surCreer={abonnements.creer}
      surModifier={(ligne, changements) => abonnements.modifier(ligne.id, changements)}
      message={message}
    />
  );
}
